import { codePrefixesService } from '../services';

/**
 * Finds a code prefix by select value. Select values are always strings while
 * Content Manager ids are numbers, so compare as strings (documentId too).
 */
export const findCodePrefix = (codePrefixes: any[], id: string | number | undefined | null) => {
  if (id === undefined || id === null || id === '') return undefined;
  return codePrefixes.find(
    (p: any) => String(p.id) === String(id) || p.documentId === id
  );
};

/**
 * The sequence number the NEXT generated document should use.
 * currentSequence stores the next free number (minimum 1, so the first
 * document of a prefix is always 001).
 */
export const nextSequence = (prefix: any): number =>
  Math.max(1, Number(prefix?.currentSequence) || 1);

/** Next full code for a prefix, e.g. "INV001" */
export const nextCode = (prefix: any, padding = 3): string =>
  `${prefix?.prefix || ''}${String(nextSequence(prefix)).padStart(padding, '0')}`;

/**
 * Highest numeric suffix actually in use for a prefix among existing codes
 * (e.g. ["EXT001","EXT066"] → 66). Non-numeric suffixes are ignored.
 */
export const maxExistingSequence = (codes: (string | undefined | null)[], prefix: string): number =>
  codes.reduce((max, code) => {
    if (!code || !code.startsWith(prefix)) return max;
    const n = parseInt(code.slice(prefix.length), 10);
    return Number.isFinite(n) ? Math.max(max, n) : max;
  }, 0);

/**
 * The next sequence that is guaranteed free: the larger of the stored
 * counter and the highest existing code + 1. currentSequence can drift
 * behind reality (bulk imports, manual codes, missed bumps), so trusting
 * it alone generates duplicates that Strapi rejects as non-unique.
 */
export const nextFreeSequence = (prefix: any, existingCodes: (string | undefined | null)[]): number =>
  Math.max(nextSequence(prefix), maxExistingSequence(existingCodes, prefix?.prefix || '') + 1);

/**
 * After a record with a generated number (e.g. FIN001) is created, advance the
 * matching code prefix so the next generated number is the used one + 1.
 * The backend does not auto-increment currentSequence.
 */
export async function bumpCodePrefixSequence(generatedNumber?: string): Promise<void> {
  if (!generatedNumber) return;
  try {
    const res: any = await codePrefixesService.getAll({ pagination: { pageSize: 100 } });
    const prefixes: any[] = res?.data || [];
    // Longest matching prefix wins (e.g. "FIN" over "F")
    const match = prefixes
      .filter((p: any) => p.prefix && generatedNumber.startsWith(p.prefix))
      .sort((a: any, b: any) => String(b.prefix).length - String(a.prefix).length)[0];
    if (!match) return;
    const seq = parseInt(generatedNumber.slice(String(match.prefix).length), 10);
    if (!Number.isFinite(seq)) return;
    if ((match.currentSequence || 0) > seq) return; // already advanced further
    // Minimal payload: documentId is what getDocumentId resolves for the URL,
    // and spreading the whole row would leak read-only fields into the PUT body.
    await codePrefixesService.update(match.id, {
      documentId: match.documentId,
      currentSequence: seq + 1,
    });
  } catch (e) {
    // Never block the main create flow on a sequence bump failure
    console.error('Failed to update code prefix sequence:', e);
  }
}
